import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Target, Mail, PenLine, LayoutTemplate, Download } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PaperPreview } from "@/components/docs/PaperPreview";
import { ResumeTemplate } from "@/components/docs/Templates";
import { SAMPLE_RESUME, TEMPLATES } from "@/lib/sample";

const BEFORE = "Worked on a new onboarding flow";
const AFTER = "Redesigned self-serve onboarding, cutting time-to-value from 6 days to 2";

const Typewriter = () => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setN((x) => (x > AFTER.length + 40 ? 0 : x + 1)), 45);
    return () => clearInterval(t);
  }, []);
  return <span className="caret">{AFTER.slice(0, n)}</span>;
};

const NavBar = () => (
  <header className="sticky top-0 z-30 border-b border-paper-border/70 bg-paper/80 backdrop-blur-md">
    <div className="mx-auto flex h-16 max-w-6xl items-center gap-8 px-4 sm:px-6">
      <Logo />
      <nav className="hidden gap-6 text-sm font-medium text-ink-soft md:flex">
        <a href="#features" className="hover:text-ink" data-testid="nav-features">Features</a>
        <a href="#templates" className="hover:text-ink" data-testid="nav-templates">Templates</a>
        <a href="#pricing" className="hover:text-ink" data-testid="nav-pricing">Pricing</a>
      </nav>
      <div className="ml-auto flex items-center gap-2">
        <Link to="/sign-in" className="whitespace-nowrap px-3 py-2 text-sm font-semibold text-ink hover:text-thread" data-testid="nav-sign-in">Sign in</Link>
        <Link to="/sign-up" className="btn-thread whitespace-nowrap !py-2" data-testid="nav-get-started">Start free</Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="paper-grain relative overflow-hidden">
    <div className="mx-auto grid max-w-6xl items-center gap-14 px-4 pb-20 pt-16 sm:px-6 grid-cols-1 lg:grid-cols-[1.05fr_1fr] lg:pb-28 lg:pt-24">
      <div className="min-w-0">
        <p className="eyebrow rise">AI resume &amp; cover letter studio</p>
        <h1 className="rise rise-1 mt-5 text-5xl leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
          Your work,<br />on one <em className="text-thread">perfect</em> page.
        </h1>
        <p className="rise rise-2 mt-7 max-w-lg text-lg leading-relaxed text-ink-soft">
          Pitchpaper turns your experience into a sharp, tailored resume and a cover letter for every job. Build and preview free. Pay only when you download.
        </p>
        <div className="rise rise-3 mt-9 flex flex-wrap gap-3">
          <Link to="/sign-up" className="btn-thread !px-6 !py-3 !text-base" data-testid="hero-get-started-btn">Build my resume free <ArrowRight className="h-4 w-4" /></Link>
          <a href="#pricing" className="btn-ink !px-6 !py-3 !text-base" data-testid="hero-pricing-btn">See pricing</a>
        </div>
        <p className="rise rise-4 mt-6 font-mono text-xs text-taupe">No card needed · 5 templates · from $6.99 AUD (incl. GST) per download</p>
      </div>
      <div className="rise rise-2 relative min-w-0">
        <div className="absolute -right-4 -top-4 h-full w-full rotate-3 rounded-sm bg-white/70 shadow-sm" />
        <div className="relative -rotate-1">
          <PaperPreview testId="hero-resume-preview"><ResumeTemplate template="modern" data={SAMPLE_RESUME} /></PaperPreview>
        </div>
        <div className="absolute -bottom-8 -left-2 max-w-[320px] rounded-xl border border-paper-border bg-white p-4 shadow-xl sm:-left-10">
          <p className="eyebrow !text-thread flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI improve</p>
          <p className="mt-2 text-sm text-taupe line-through">{BEFORE}</p>
          <p className="mt-1 min-h-[40px] text-sm font-medium text-ink"><Typewriter /></p>
        </div>
      </div>
    </div>
  </section>
);

const FEATURES = [
  { icon: Sparkles, title: "Improve any line", body: "One click rewrites a flat summary or weak bullets into strong, result-focused writing. You choose whether to keep it.", wide: true },
  { icon: Target, title: "Tailor to the job", body: "Paste a posting. Your summary and skills shift to match what the employer is looking for." },
  { icon: Mail, title: "Cover letters in seconds", body: "Pick professional, warm or confident. Watch the letter write itself from your resume." },
  { icon: LayoutTemplate, title: "Five crafted templates", body: "Classic, Modern, Minimal, Bold and Vivid. Switch instantly and the live preview updates as you type." },
  { icon: Download, title: "Pay per download", body: "Everything is free until you're ready. Then $6.99 AUD for one clean PDF, or a 30-day pass.", wide: true },
];

const Features = () => (
  <section id="features" className="border-y border-paper-border bg-white py-20 lg:py-28">
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <p className="eyebrow">What it does</p>
      <h2 className="mt-3 max-w-2xl text-4xl leading-tight text-ink sm:text-5xl">A writing partner that knows what hiring managers read.</h2>
      <div className="mt-14 grid gap-4 md:grid-cols-3">
        {FEATURES.map((f) => (
          <div key={f.title} className={`group rounded-2xl border border-paper-border bg-paper p-7 transition-colors hover:border-thread/40 ${f.wide ? "md:col-span-2" : ""}`}>
            <f.icon className="h-6 w-6 text-thread transition-transform duration-300 group-hover:-translate-y-0.5" />
            <h3 className="mt-6 font-sans text-lg font-semibold text-ink">{f.title}</h3>
            <p className="mt-2 max-w-md leading-relaxed text-ink-soft">{f.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Templates = () => {
  const [t, setT] = useState("classic");
  return (
    <section id="templates" className="py-20 lg:py-28">
      <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 grid-cols-1 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <p className="eyebrow">Templates</p>
          <h2 className="mt-3 text-4xl leading-tight text-ink sm:text-5xl">Five styles. Each one recruiter-ready.</h2>
          <div className="mt-10 space-y-3">
            {TEMPLATES.map((x, i) => (
              <button key={x.id} onClick={() => setT(x.id)} data-testid={`landing-template-${x.id}`}
                className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors ${t === x.id ? "border-thread bg-white shadow-sm" : "border-paper-border hover:bg-white/60"}`}>
                <span className="font-mono text-xs text-taupe">0{i + 1}</span>
                <span><span className="block font-semibold text-ink">{x.name}</span><span className="text-sm text-ink-soft">{x.note}</span></span>
              </button>
            ))}
          </div>
        </div>
        <div className="paper-grain rounded-2xl bg-paper-subtle p-6 sm:p-10">
          <PaperPreview testId="landing-template-preview"><ResumeTemplate template={t} data={SAMPLE_RESUME} /></PaperPreview>
        </div>
      </div>
    </section>
  );
};

const PLANS = [
  { name: "Free", price: "$0", note: "Forever", perks: ["Unlimited resumes & cover letters", "AI improve, tailor and letter writing", "Live preview with watermark"], cta: "Start building" },
  { name: "Single Download", price: "$6.99", note: "One-time, per document, incl. GST", perks: ["One watermark-free PDF", "Re-download any time", "Keep editing after paying"], cta: "Start free, pay later" },
  { name: "30-Day Pass", price: "$19.99", note: "One-time, no subscription, incl. GST", perks: ["Unlimited clean downloads", "Every resume and letter", "Ideal for an active job hunt"], cta: "Start free, pay later", best: true },
];

const Pricing = () => (
  <section id="pricing" className="border-t border-paper-border bg-white py-20 lg:py-28">
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <p className="eyebrow">Pricing</p>
      <h2 className="mt-3 text-4xl text-ink sm:text-5xl">Free to write. Simple to download.</h2>
      <p className="mt-4 max-w-xl text-ink-soft">All prices in Australian dollars and include GST. No subscriptions, no surprise renewals. Pay securely with Stripe only when your document is ready.</p>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {PLANS.map((p) => (
          <div key={p.name} className={`relative flex flex-col rounded-2xl border p-7 ${p.best ? "border-thread bg-paper shadow-lg" : "border-paper-border bg-paper"}`} data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
            {p.best && <span className="absolute -top-3 left-7 rounded-full bg-thread px-3 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white">Best value</span>}
            <p className="font-semibold text-ink">{p.name}</p>
            <p className="mt-4 font-serif text-5xl text-ink" data-testid={`pricing-price-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>{p.price}<span className="ml-2 align-middle font-mono text-sm text-taupe">AUD</span></p>
            <p className="mt-1 text-sm text-taupe">{p.note}</p>
            <ul className="mt-6 flex-1 space-y-3 text-sm text-ink-soft">
              {p.perks.map((x) => <li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-thread" />{x}</li>)}
            </ul>
            <Link to="/sign-up" className={`${p.best ? "btn-thread" : "btn-ink"} mt-8`}>{p.cta}</Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="py-20">
    <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 md:grid-cols-3">
      {[[PenLine, "Write", "Fill in your story section by section, with AI help on every line."], [Target, "Tailor", "Match the resume to each job and generate a letter to go with it."], [Download, "Send", "Download a clean PDF and apply with confidence."]].map(([I, t, b], i) => (
        <div key={t} className="border-t border-ink pt-6">
          <span className="font-mono text-xs text-taupe">STEP 0{i + 1}</span>
          <h3 className="mt-3 flex items-center gap-2 text-3xl text-ink">{t} <I className="h-5 w-5 text-thread" /></h3>
          <p className="mt-2 text-ink-soft">{b}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="bg-paper" data-testid="landing-page">
      <NavBar />
      <Hero />
      <Steps />
      <Features />
      <Templates />
      <Pricing />
      <section className="bg-ink py-20 text-paper">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 px-4 sm:px-6 md:flex-row md:items-center md:justify-between">
          <h2 className="max-w-xl text-4xl leading-tight text-paper sm:text-5xl">Your next job starts with one good page.</h2>
          <Link to="/sign-up" className="btn-thread !px-6 !py-3 !text-base" data-testid="footer-cta-btn">Start free <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
      <footer className="border-t border-paper-border py-10">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <Logo size={26} />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs text-taupe">
            <span>© {new Date().getFullYear()} Pitchpaper · ABN 87 571 984 745</span>
            <span className="flex gap-4">
              <Link to="/terms" className="hover:text-thread hover:underline" data-testid="footer-terms-link">Terms</Link>
              <Link to="/privacy" className="hover:text-thread hover:underline" data-testid="footer-privacy-link">Privacy</Link>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
